<script lang="ts">
	import { onMount, type Component } from 'svelte';
	import { THEMES, loadTheme, saveTheme, type ThemeName } from './themes';
	import { executeCommand } from './commands/registry';
	import type { CommandContext } from './commands/types';
	import { t as createT } from '$lib/i18n/index';
	import type { Language } from '$lib/i18n/index';
	import AsciiLogo from './AsciiLogo.svelte';
	import { handleCommandResult, type HistoryLine } from './handlers';
	import { trapFocus } from '$lib/utils/focus-trap';
	import { captureEvent } from '$lib/analytics/posthog';
	import type Anthropic from '@anthropic-ai/sdk';

	// Lazy-load overlay map
	const overlayMap: Record<string, () => Promise<{ default: Component<any> }>> = {
		// Games
		snake: () => import('./games/Snake.svelte'),
		tetris: () => import('./games/Tetris.svelte'),
		pong: () => import('./games/Pong.svelte'),
		guess: () => import('./games/Guess.svelte'),
		breakout: () => import('./games/Breakout.svelte'),
		tictactoe: () => import('./games/TicTacToe.svelte'),
		typing: () => import('./games/Typing.svelte'),
		challenge: () => import('./games/CodingChallenge.svelte'),
		// Animations
		matrix: () => import('./animations/Matrix.svelte'),
		fireworks: () => import('./animations/Fireworks.svelte'),
		hack: () => import('./animations/Hack.svelte'),
		glitch: () => import('./animations/Glitch.svelte'),
		rain: () => import('./animations/Rain.svelte'),
		particles: () => import('./animations/Particles.svelte'),
		typewriter: () => import('./animations/Typewriter.svelte'),
		nyancat: () => import('./animations/NyanCat.svelte'),
		coffee: () => import('./animations/Coffee.svelte'),
		// Tools
		pomodoro: () => import('./tools/Pomodoro.svelte'),
		timer: () => import('./tools/Timer.svelte'),
		json: () => import('./tools/JsonFormatter.svelte')
	};

	interface Props {
		onClose: () => void;
		lang?: Language;
		source?: string;
	}

	let { onClose, lang = 'en', source = 'unknown' }: Props = $props();

	const tr = $derived(createT(lang));

	// Terminal state
	let input = $state('');
	let history = $state<HistoryLine[]>([]);
	let inputElement = $state<HTMLInputElement | null>(null);
	let terminalContent = $state<HTMLDivElement | null>(null);

	// Overlay state
	type OverlayType =
		| 'snake'
		| 'tetris'
		| 'pong'
		| 'guess'
		| 'breakout'
		| 'tictactoe'
		| 'typing'
		| 'matrix'
		| 'fireworks'
		| 'hack'
		| 'glitch'
		| 'rain'
		| 'particles'
		| 'typewriter'
		| 'nyancat'
		| 'coffee'
		| 'pomodoro'
		| 'timer'
		| 'json'
		| null;
	let activeOverlay = $state<OverlayType>(null);
	let timerInitialTime = $state<string | undefined>(undefined);

	// Theme state
	let currentTheme = $state<ThemeName>('matrix');
	const theme = $derived(THEMES[currentTheme]);

	// Command history
	let commandHistory = $state<string[]>([]);
	let historyIndex = $state(-1);
	let tempInput = $state('');
	const MAX_HISTORY = 50;

	// Timing constants (milliseconds)
	const TIMING = {
		LOGO_ANIMATION_DELAY: 800,
		BOOT_LINE_FAST: 80,
		BOOT_LINE_SLOW: 20,
		SCROLL_DELAY: 10,
		OVERLAY_CLOSE_FOCUS: 50,
		EFFECT_DURATION: 2000
	} as const;

	// AI chat conversation history
	let chatMessages = $state<Anthropic.MessageParam[]>([]);

	// Effects
	let activeEffect = $state<string | null>(null);
	let crtMode = $state(false);

	// Suggested prompts
	let showSuggestions = $state(true);
	let hasExecutedCommand = $state(false);

	// Show ASCII logo after boot
	let showLogo = $state(false);

	// Streaming state for screen reader announcements
	let isStreaming = $state(false);

	// Container ref for focus trap
	let containerEl = $state<HTMLDivElement | null>(null);

	// Character limit
	const CHAR_LIMIT = 10000;

	// Konami code detection
	let konamiSequence = $state<string[]>([]);
	const KONAMI_CODE = [
		'ArrowUp',
		'ArrowUp',
		'ArrowDown',
		'ArrowDown',
		'ArrowLeft',
		'ArrowRight',
		'ArrowLeft',
		'ArrowRight',
		'KeyB',
		'KeyA'
	];

	// Command context for handlers
	const commandContext: CommandContext = {
		get currentTheme() {
			return currentTheme;
		},
		setTheme: (name: ThemeName) => {
			if (THEMES[name]) {
				currentTheme = name;
				saveTheme(currentTheme);
			}
		}
	};

	function loadCommandHistory() {
		try {
			const stored = localStorage.getItem('terminal-command-history');
			if (stored) commandHistory = JSON.parse(stored);
		} catch {
			/* ignored */
		}
	}

	function saveCommandHistory() {
		try {
			localStorage.setItem(
				'terminal-command-history',
				JSON.stringify(commandHistory.slice(-MAX_HISTORY))
			);
		} catch {
			/* ignored */
		}
	}

	// Boot sequence
	onMount(() => {
		currentTheme = loadTheme();
		loadCommandHistory();
		captureEvent('terminal_opened', { source });

		// Show ASCII logo immediately
		showLogo = true;

		const bootLines = [
			'',
			tr('terminal_boot_bios'),
			tr('terminal_boot_copyright').replace('{year}', String(new Date().getFullYear())),
			'',
			tr('terminal_boot_kernel'),
			tr('terminal_boot_modules'),
			tr('terminal_boot_filesystems'),
			tr('terminal_boot_services'),
			'',
			'╔═══════════════════════════════════════════════════════════╗',
			'║  AMETA TERMINAL v2.0                                      ║',
			`║  ${tr('terminal_boot_help')}                       ║`,
			`║  ${tr('terminal_boot_games')}                      ║`,
			'╚═══════════════════════════════════════════════════════════╝',
			''
		];

		// Delay boot text to allow logo animation to play
		let delay = TIMING.LOGO_ANIMATION_DELAY;
		bootLines.forEach((line, i) => {
			setTimeout(() => {
				history = [...history, { type: 'output', text: line }];
				scrollToBottom();
			}, delay);
			delay += i < 7 ? TIMING.BOOT_LINE_FAST : TIMING.BOOT_LINE_SLOW;
		});

		setTimeout(() => inputElement?.focus(), delay);
		window.addEventListener('keydown', handleGlobalKeydown);
		const releaseTrap = containerEl ? trapFocus(containerEl) : () => {};
		return () => {
			window.removeEventListener('keydown', handleGlobalKeydown);
			releaseTrap();
		};
	});

	function scrollToBottom() {
		setTimeout(() => {
			if (terminalContent) {
				terminalContent.scrollTop = terminalContent.scrollHeight;
			}
		}, TIMING.SCROLL_DELAY);
	}

	function closeOverlay() {
		activeOverlay = null;
		timerInitialTime = undefined;
		setTimeout(() => inputElement?.focus(), TIMING.OVERLAY_CLOSE_FOCUS);
	}

	function applyEffect(effect: string) {
		if (effect === 'crt') {
			crtMode = !crtMode;
			history = [
				...history,
				{ type: 'output', text: `CRT mode ${crtMode ? 'enabled' : 'disabled'}.` }
			];
			return;
		}
		activeEffect = `effect-${effect}`;
		setTimeout(() => {
			activeEffect = null;
		}, TIMING.EFFECT_DURATION);
	}

	function handleSuggestion(cmd: string) {
		input = cmd;
		handleSubmit(new Event('submit'));
	}

	async function handleSubmit(e: Event) {
		e.preventDefault();
		const rawInput = input.trim();

		if (rawInput.length > CHAR_LIMIT) return;

		history = [...history, { type: 'input', text: `$ ${rawInput}` }];

		if (rawInput) {
			// Save to command history
			commandHistory = [...commandHistory.filter((c) => c !== rawInput), rawInput].slice(
				-MAX_HISTORY
			);
			saveCommandHistory();
			historyIndex = -1;
			tempInput = '';
			hasExecutedCommand = true;
			showSuggestions = false;

			const result = executeCommand(rawInput, commandContext);

			if (result) {
				await handleCommandResult(result, {
					history,
					chatMessages,
					scrollToBottom,
					setHistory: (h) => {
						history = h;
					},
					setChatMessages: (m) => {
						chatMessages = m;
					},
					setActiveOverlay: (name) => {
						activeOverlay = name as typeof activeOverlay;
					},
					setTimerInitialTime: (t) => {
						timerInitialTime = t;
					},
					setStreaming: (s) => {
						isStreaming = s;
					},
					applyEffect,
					onClose: handleClose
				});
			}
		}

		history = [...history, { type: 'output', text: '' }];
		input = '';
		scrollToBottom();
	}

	function handleInputKeydown(e: KeyboardEvent) {
		// Command history navigation
		if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (commandHistory.length === 0) return;
			if (historyIndex === -1) tempInput = input;
			historyIndex = Math.min(historyIndex + 1, commandHistory.length - 1);
			input = commandHistory[commandHistory.length - 1 - historyIndex];
			return;
		}
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (historyIndex <= -1) return;
			historyIndex--;
			input =
				historyIndex === -1 ? tempInput : commandHistory[commandHistory.length - 1 - historyIndex];
			return;
		}

		// Keyboard shortcuts
		if (e.ctrlKey) {
			const el = inputElement;
			if (!el) return;
			switch (e.key) {
				case 'a':
					e.preventDefault();
					el.setSelectionRange(0, 0);
					break;
				case 'e':
					e.preventDefault();
					el.setSelectionRange(input.length, input.length);
					break;
				case 'k':
					e.preventDefault();
					input = input.slice(0, el.selectionStart ?? 0);
					break;
				case 'u':
					e.preventDefault();
					{
						const pos = el.selectionStart ?? 0;
						input = input.slice(pos);
						setTimeout(() => el.setSelectionRange(0, 0), 0);
					}
					break;
				case 'w':
					e.preventDefault();
					{
						const pos = el.selectionStart ?? 0;
						const before = input.slice(0, pos);
						const trimmed = before.trimEnd();
						const lastSpace = trimmed.lastIndexOf(' ');
						const newPos = lastSpace === -1 ? 0 : lastSpace + 1;
						input = input.slice(0, newPos) + input.slice(pos);
						setTimeout(() => el.setSelectionRange(newPos, newPos), 0);
					}
					break;
			}
		}
	}

	function handleClose() {
		captureEvent('terminal_closed');
		onClose();
	}

	function handleGlobalKeydown(e: KeyboardEvent) {
		// Escape to close
		if (e.key === 'Escape') {
			if (activeOverlay) {
				closeOverlay();
			} else {
				handleClose();
			}
			return;
		}

		// Konami code detection (only when no overlay)
		if (!activeOverlay) {
			konamiSequence = [...konamiSequence, e.code].slice(-10);
			if (konamiSequence.join(',') === KONAMI_CODE.join(',')) {
				history = [
					...history,
					{ type: 'output', text: '' },
					{ type: 'ascii', text: 'KONAMI CODE ACTIVATED!' },
					{ type: 'output', text: '+30 lives! (Just kidding, this is a website)' },
					{ type: 'output', text: '' }
				];
				konamiSequence = [];
				scrollToBottom();
			}
		}
	}

	const charPct = $derived((input.length / CHAR_LIMIT) * 100);

	// Lazy-load overlay component
	let overlayComponent = $derived(activeOverlay ? overlayMap[activeOverlay]?.() : null);
</script>

<div
	bind:this={containerEl}
	class="terminal-container fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4"
	onclick={(e) => e.target === e.currentTarget && !activeOverlay && handleClose()}
	onkeydown={(e) =>
		e.key === 'Escape' && e.target === e.currentTarget && !activeOverlay && handleClose()}
	style="--term-primary: {theme.primary}; --term-secondary: {theme.secondary}; --term-accent: {theme.accent}; --term-bg: {theme.bg}; --term-bg-light: {theme.bgLight}; --term-text: {theme.text}; --term-text-muted: {theme.textMuted};"
	role="dialog"
	aria-label="Terminal"
	tabindex="-1"
>
	<!-- Skip link -->
	<a
		href="#terminal-input"
		class="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-1 focus:bg-slate-800 focus:text-white focus:rounded"
	>
		Skip to terminal input
	</a>

	<!-- Screen reader announcements for streaming -->
	<div class="sr-only" aria-live="polite" aria-atomic="true">
		{#if isStreaming}
			AI is responding...
		{/if}
	</div>

	<div
		class="w-full max-w-6xl h-[85vh] rounded-xl border border-slate-700 shadow-2xl overflow-hidden animate-scale-in flex flex-col {activeEffect ||
			''} {crtMode ? 'crt-mode' : ''}"
		style="background-color: var(--term-bg-light);"
	>
		<!-- Title bar -->
		<div
			class="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700 flex-shrink-0"
		>
			<div class="flex items-center gap-3">
				<div class="flex gap-2">
					<button
						onclick={() => (activeOverlay ? closeOverlay() : handleClose())}
						class="w-3 h-3 rounded-full bg-red-500 hover:bg-red-400 transition-colors"
						aria-label="Close"
					></button>
					<div class="w-3 h-3 rounded-full bg-yellow-500"></div>
					<div class="w-3 h-3 rounded-full bg-green-500"></div>
				</div>
				<span class="text-sm text-slate-400 font-mono"
					>ameta@terminal ~ {activeOverlay ? `[${activeOverlay}]` : ''}</span
				>
			</div>
			<span class="text-xs text-slate-500">ESC to {activeOverlay ? 'exit' : 'close'}</span>
		</div>

		<!-- Content -->
		{#if activeOverlay && overlayComponent}
			{#await overlayComponent}
				<div class="flex-1 flex items-center justify-center" style="color: var(--term-primary);">
					Loading...
				</div>
			{:then module}
				{#if activeOverlay === 'timer'}
					<module.default onClose={closeOverlay} initialTime={timerInitialTime} />
				{:else}
					<module.default onClose={closeOverlay} />
				{/if}
			{:catch}
				<div class="flex-1 flex items-center justify-center text-red-400">
					Failed to load component.
				</div>
			{/await}
		{:else}
			<!-- Normal Terminal -->
			<div
				bind:this={terminalContent}
				class="flex-1 min-h-[400px] max-h-[75vh] overflow-y-auto p-4 font-mono text-sm"
				style="background-color: var(--term-bg);"
				role="log"
				aria-live="polite"
				aria-relevant="additions"
			>
				{#if showLogo}
					<div class="mb-2">
						<AsciiLogo />
					</div>
				{/if}
				{#each history as line}
					{#if line.type === 'input'}
						<div class="whitespace-pre-wrap" style="color: var(--term-primary);">{line.text}</div>
					{:else if line.type === 'error'}
						<div class="whitespace-pre-wrap text-red-400">{line.text}</div>
					{:else if line.type === 'ascii'}
						<div class="whitespace-pre font-mono text-xs" style="color: var(--term-primary);">
							{line.text}
						</div>
					{:else}
						<div class="whitespace-pre-wrap" style="color: var(--term-text);">{line.text}</div>
					{/if}
				{/each}

				<!-- Suggested prompts -->
				{#if showSuggestions && !hasExecutedCommand}
					<div class="flex gap-2 pt-2 pb-2 flex-wrap">
						{#each ['help', 'games', 'about'] as suggestion}
							<button
								onclick={() => handleSuggestion(suggestion)}
								class="px-3 py-1 rounded-full text-xs border transition-colors hover:opacity-80"
								style="border-color: var(--term-primary); color: var(--term-primary);"
							>
								{suggestion}
							</button>
						{/each}
					</div>
				{/if}

				<form
					onsubmit={handleSubmit}
					class="flex items-center gap-2 pt-4 pb-4"
					style="color: var(--term-primary);"
				>
					<span class="hidden sm:inline">$</span>
					<span class="sm:hidden">{'>'}</span>
					<input
						id="terminal-input"
						bind:this={inputElement}
						bind:value={input}
						onkeydown={handleInputKeydown}
						type="text"
						class="flex-1 bg-transparent outline-none"
						style:color="var(--term-text)"
						style="caret-color: var(--term-primary);"
						autocomplete="off"
						autocapitalize="off"
						spellcheck="false"
						maxlength={CHAR_LIMIT}
					/>
					<!-- Mobile send button -->
					<button
						type="submit"
						class="sm:hidden px-3 py-1 rounded text-xs"
						style="background: var(--term-primary); color: var(--term-bg);"
					>
						Send
					</button>
				</form>

				<!-- Character limit indicator -->
				{#if charPct >= 90}
					<div class="text-xs pb-2" style="color: {charPct >= 100 ? '#ef4444' : '#fbbf24'};">
						{input.length}/{CHAR_LIMIT}
					</div>
				{/if}
			</div>
		{/if}
	</div>
</div>
